/**
 * @fileoverview Mail list pane for a job mailbox (MOR-529).
 *
 * One list pane for the whole mail surface, like a normal client: search, a
 * filter menu (job + unread on the workspace inbox; unread + contact role on
 * per-job thread folders), and a folder switcher (Inbox / Sent / For Review /
 * Archive). Thread folders render contact-aware rows with hover actions
 * (read/unread toggle, archive, snooze); Sent renders outbound messages with
 * delivery status; Drafts renders the approval queue (pending first).
 */

import { useMemo, useState } from "react";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import {
  Archive,
  ArchiveRestore,
  Check,
  Clock,
  ListFilter,
  Mail as MailIcon,
  MailOpen,
  Search,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Spinner } from "@/ui/primitives/spinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { ROLE_META, type ContactRole } from "@/lib/contact-roles";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { avatarTint, initialsFor } from "./avatar";
import { deliveryLabel } from "./deliveryLabel";
import { draftStatusMeta } from "./draftStatus";
import type {
  MailDraft,
  MailThread,
  SentMessage,
  UserDraftListItem,
} from "./types";

export type MailFolder = "all" | "sent" | "userDrafts" | "drafts" | "archive";

const FOLDERS: Array<{ key: MailFolder; label: string }> = [
  { key: "all", label: "Inbox" },
  { key: "sent", label: "Sent" },
  // Human works-in-progress, autosaved from the composers (MOR-783).
  { key: "userDrafts", label: "Drafts" },
  // Morgan's drafts awaiting practitioner approval.
  { key: "drafts", label: "For Review" },
  { key: "archive", label: "Archive" },
];

/** Snooze presets, computed at click time. */
function snoozePresets(): Array<{ label: string; until: number }> {
  const now = new Date();
  const laterToday = new Date(now.getTime() + 4 * 60 * 60 * 1000);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const nextWeek = new Date(now);
  nextWeek.setDate(nextWeek.getDate() + ((8 - nextWeek.getDay()) % 7 || 7));
  nextWeek.setHours(9, 0, 0, 0);
  return [
    { label: "Later today", until: laterToday.getTime() },
    { label: "Tomorrow, 9am", until: tomorrow.getTime() },
    { label: "Next week, Mon 9am", until: nextWeek.getTime() },
  ];
}

const DELIVERY_BADGE: Record<
  string,
  { label: string; dotClassName: string; textClassName: string }
> = {
  queued: {
    label: "Queued",
    dotClassName: "bg-muted-foreground/50",
    textClassName: "text-muted-foreground",
  },
  sent: {
    label: "Sent",
    dotClassName: "bg-emerald-500",
    textClassName: "text-emerald-700 dark:text-emerald-400",
  },
  delivered: {
    label: "Delivered",
    dotClassName: "bg-emerald-500",
    textClassName: "text-emerald-700 dark:text-emerald-400",
  },
  bounced: {
    label: "Bounced",
    dotClassName: "bg-destructive",
    textClassName: "text-destructive",
  },
  failed: {
    label: "Failed",
    dotClassName: "bg-destructive",
    textClassName: "text-destructive",
  },
  // Read through `deliveryLabel`: the detail says whether it was spam.
  complained: {
    label: "Complaint",
    dotClassName: "bg-destructive",
    textClassName: "text-destructive",
  },
};

interface MailListPaneProps {
  /** Job scope; omit for the workspace-wide inbox (all jobs, job badges). */
  jobId?: Id<"jobs">;
  folder: MailFolder;
  onFolderChange: (folder: MailFolder) => void;
  selectedThreadId: Id<"mailThreads"> | null;
  onSelectThread: (threadId: Id<"mailThreads">, jobId: Id<"jobs">) => void;
  selectedDraftId: Id<"mailDrafts"> | null;
  onSelectDraft: (draftId: Id<"mailDrafts">, jobId: Id<"jobs">) => void;
  selectedUserDraftId: Id<"mailDrafts"> | null;
  onSelectUserDraft: (draft: UserDraftListItem) => void;
  pendingDraftCount: number;
  /** Unread-thread count for the Inbox tab badge. */
  unreadCount: number;
  /** User-draft count for the Drafts tab badge. */
  userDraftCount: number;
}

export function MailListPane({
  jobId,
  folder,
  onFolderChange,
  selectedThreadId,
  onSelectThread,
  selectedDraftId,
  onSelectDraft,
  selectedUserDraftId,
  onSelectUserDraft,
  pendingDraftCount,
  unreadCount,
  userDraftCount,
}: MailListPaneProps) {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  /** Workspace-only: narrow the aggregate inbox to one job's mailbox. */
  const [jobFilter, setJobFilter] = useState<{
    id: Id<"jobs">;
    name: string;
  } | null>(null);
  // "Unread" is a filter on the inbox rather than its own folder.
  const [unreadOnly, setUnreadOnly] = useState(false);

  const isThreadFolder = folder === "all" || folder === "archive";
  const isWorkspace = !jobId;
  const threadView = folder === "archive" ? "archive" : "all";

  const threadsQuery = usePaginatedQuery(
    api.mail.queries.listThreads,
    isThreadFolder && jobId ? { jobId, view: threadView } : "skip",
    { initialNumItems: 25 },
  );
  const workspaceThreads = useQuery(
    api.mail.queries.listWorkspaceThreads,
    isThreadFolder && !jobId ? { view: threadView } : "skip",
  );
  const sentQuery = usePaginatedQuery(
    api.mail.queries.listSentMessages,
    folder === "sent" && jobId ? { jobId } : "skip",
    { initialNumItems: 25 },
  );
  const workspaceSent = useQuery(
    api.mail.queries.listWorkspaceSentMessages,
    folder === "sent" && !jobId ? {} : "skip",
  );
  const drafts: MailDraft[] | undefined = useQuery(
    jobId ? api.mail.drafts.listDrafts : api.mail.drafts.listWorkspaceDrafts,
    folder === "drafts" ? (jobId ? { jobId } : {}) : "skip",
  );
  const userDrafts: UserDraftListItem[] | undefined = useQuery(
    jobId
      ? api.mail.userDrafts.listUserDrafts
      : api.mail.userDrafts.listWorkspaceUserDrafts,
    folder === "userDrafts" ? (jobId ? { jobId } : {}) : "skip",
  );

  const threads = useMemo<MailThread[]>(
    () => (jobId ? (threadsQuery.results ?? []) : (workspaceThreads ?? [])),
    [jobId, threadsQuery.results, workspaceThreads],
  );

  const rawSentMessages = useMemo<SentMessage[]>(
    () => (jobId ? (sentQuery.results ?? []) : (workspaceSent ?? [])),
    [jobId, sentQuery.results, workspaceSent],
  );

  const jobFilterId = jobFilter?.id ?? null;

  // Jobs present in the active folder — drives the workspace Job filter.
  // Always include the active selection so it stays visible after folder switches.
  const availableJobs = useMemo<Array<{ id: Id<"jobs">; name: string }>>(() => {
    if (!isWorkspace) return [];
    const byId = new Map<Id<"jobs">, string>();
    const add = (id: Id<"jobs">, name?: string) => {
      if (!byId.has(id)) byId.set(id, name?.trim() || "Unknown job");
    };
    if (isThreadFolder) {
      for (const thread of threads) add(thread.jobId, thread.jobName);
    } else if (folder === "sent") {
      for (const message of rawSentMessages)
        add(message.jobId, message.jobName);
    } else if (folder === "drafts") {
      for (const draft of drafts ?? []) add(draft.jobId, draft.jobName);
    } else if (folder === "userDrafts") {
      for (const draft of userDrafts ?? []) add(draft.jobId, draft.jobName);
    }
    if (jobFilter) add(jobFilter.id, jobFilter.name);
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    isWorkspace,
    isThreadFolder,
    folder,
    threads,
    rawSentMessages,
    drafts,
    userDrafts,
    jobFilter,
  ]);

  // Scope lists by job before role/search filters so role options stay relevant.
  const jobScopedThreads = useMemo(() => {
    if (!jobFilterId) return threads;
    return threads.filter((thread) => thread.jobId === jobFilterId);
  }, [threads, jobFilterId]);

  // Role options offered by the filter — only roles actually present.
  const availableRoles = useMemo(() => {
    const roles = new Set<string>();
    for (const thread of jobScopedThreads) {
      for (const participant of thread.participants ?? []) {
        for (const role of participant.roles) roles.add(role);
      }
    }
    return [...roles].sort();
  }, [jobScopedThreads]);

  const q = search.trim().toLowerCase();
  const hasActiveFilters = Boolean(
    jobFilterId || (!isWorkspace && roleFilter) || unreadOnly,
  );

  const visibleThreads = useMemo(() => {
    return jobScopedThreads.filter((thread) => {
      if (unreadOnly && thread.unreadCount === 0) return false;
      // Role filter is per-job mailbox only.
      if (!isWorkspace && roleFilter) {
        const hasRole = (thread.participants ?? []).some((p) =>
          p.roles.includes(roleFilter),
        );
        if (!hasRole) return false;
      }
      if (!q) return true;
      const haystack = [
        thread.subject,
        thread.snippet,
        ...thread.participantEmails,
        ...(thread.participants ?? []).map((p) => p.displayName ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [jobScopedThreads, q, roleFilter, unreadOnly, isWorkspace]);

  const sentMessages = useMemo(() => {
    const rows = jobFilterId
      ? rawSentMessages.filter((m) => m.jobId === jobFilterId)
      : rawSentMessages;
    if (!q) return rows;
    return rows.filter((m) =>
      [m.subject, m.bodyText, ...m.toAddresses]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rawSentMessages, jobFilterId, q]);

  // Drafts grouped by actionability: the approval queue on top, everything
  // else (sent / failed / rejected / superseded) below it as history.
  const draftGroups = useMemo(() => {
    const baseDrafts = drafts ?? [];
    const all = jobFilterId
      ? baseDrafts.filter((d) => d.jobId === jobFilterId)
      : baseDrafts;
    const filtered = q
      ? all.filter((d) =>
          [d.subject, d.bodyText, ...d.to].join(" ").toLowerCase().includes(q),
        )
      : all;
    const byNewest = (a: MailDraft, b: MailDraft) =>
      b._creationTime - a._creationTime;
    return {
      needsReview: filtered
        .filter((d) => d.status === "pending" || d.status === "approved")
        .sort(byNewest),
      history: filtered
        .filter((d) => d.status !== "pending" && d.status !== "approved")
        .sort(byNewest),
    };
  }, [drafts, jobFilterId, q]);
  const visibleDraftCount =
    draftGroups.needsReview.length + draftGroups.history.length;

  const visibleUserDrafts = useMemo(() => {
    const baseUserDrafts = userDrafts ?? [];
    const all = jobFilterId
      ? baseUserDrafts.filter((d) => d.jobId === jobFilterId)
      : baseUserDrafts;
    if (!q) return all;
    return all.filter((d) =>
      [d.subject, d.bodyText, ...d.to].join(" ").toLowerCase().includes(q),
    );
  }, [userDrafts, jobFilterId, q]);

  const loading = jobId
    ? (isThreadFolder && threadsQuery.status === "LoadingFirstPage") ||
      (folder === "sent" && sentQuery.status === "LoadingFirstPage") ||
      (folder === "drafts" && drafts === undefined) ||
      (folder === "userDrafts" && userDrafts === undefined)
    : (isThreadFolder && workspaceThreads === undefined) ||
      (folder === "sent" && workspaceSent === undefined) ||
      (folder === "drafts" && drafts === undefined) ||
      (folder === "userDrafts" && userDrafts === undefined);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Search + tag filter */}
      <div className="space-y-2 px-3 pt-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, subject..."
              className="h-8 pl-8 text-sm"
            />
          </div>
          {(isThreadFolder || isWorkspace) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={hasActiveFilters ? "secondary" : "outline"}
                  size="sm"
                  className="h-8 max-w-[9.5rem] gap-1.5 px-2.5 text-xs"
                >
                  <ListFilter className="size-3.5 shrink-0" />
                  <span className="truncate">
                    {jobFilter
                      ? jobFilter.name
                      : !isWorkspace && roleFilter
                        ? (ROLE_META[roleFilter as ContactRole]?.label ??
                          roleFilter)
                        : unreadOnly
                          ? "Unread"
                          : "Filter"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {isWorkspace && (
                  <>
                    <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      Job
                    </DropdownMenuLabel>
                    {availableJobs.length === 0 ? (
                      <p className="px-2 py-1.5 text-xs text-muted-foreground">
                        No job mailboxes in this folder yet.
                      </p>
                    ) : (
                      availableJobs.map((job) => (
                        <DropdownMenuItem
                          key={job.id}
                          onClick={() =>
                            setJobFilter((current) =>
                              current?.id === job.id ? null : job,
                            )
                          }
                          className={cn(
                            "text-sm",
                            jobFilterId === job.id &&
                              "bg-accent/20 font-medium",
                          )}
                        >
                          <Check
                            className={cn(
                              "mr-1.5 size-3.5 shrink-0",
                              jobFilterId === job.id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          <span className="truncate">{job.name}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                    {isThreadFolder && <DropdownMenuSeparator />}
                  </>
                )}
                {isThreadFolder && (
                  <>
                    <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      Show
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => setUnreadOnly((v) => !v)}
                      className={cn(
                        "text-sm",
                        unreadOnly && "bg-accent/20 font-medium",
                      )}
                    >
                      <Check
                        className={cn(
                          "mr-1.5 size-3.5",
                          unreadOnly ? "opacity-100" : "opacity-0",
                        )}
                      />
                      Unread only
                    </DropdownMenuItem>
                    {/* Contact role only on the per-job mailbox — roles are
                        job-scoped and don't map cleanly across the aggregate. */}
                    {!isWorkspace && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                          Contact role
                        </DropdownMenuLabel>
                        {availableRoles.length === 0 && (
                          <p className="px-2 py-1.5 text-xs text-muted-foreground">
                            No tagged contacts yet.
                          </p>
                        )}
                        {availableRoles.map((role) => {
                          const meta = ROLE_META[role as ContactRole];
                          return (
                            <DropdownMenuItem
                              key={role}
                              onClick={() =>
                                setRoleFilter((current) =>
                                  current === role ? null : role,
                                )
                              }
                              className={cn(
                                "text-sm",
                                roleFilter === role &&
                                  "bg-accent/20 font-medium",
                              )}
                            >
                              {meta?.label ?? role}
                            </DropdownMenuItem>
                          );
                        })}
                      </>
                    )}
                  </>
                )}
                {hasActiveFilters && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        setJobFilter(null);
                        setRoleFilter(null);
                        setUnreadOnly(false);
                      }}
                      className="text-sm text-muted-foreground"
                    >
                      <X className="mr-1.5 size-3.5" />
                      Clear filters
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Folder switcher */}
        <div className="grid grid-cols-5 gap-0.5 rounded-lg bg-muted p-0.5">
          {FOLDERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onFolderChange(f.key)}
              className={cn(
                "relative rounded-md px-1 py-1.5 text-xs font-medium transition-colors",
                folder === f.key
                  ? "bg-white text-foreground shadow-sm dark:bg-card"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {f.label}
              {f.key === "all" && unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-1 rounded-full bg-primary px-1 py-0.5 text-[9px] font-medium leading-none text-primary-foreground">
                  {unreadCount}
                </span>
              )}
              {f.key === "userDrafts" && userDraftCount > 0 && (
                <span className="absolute -right-0.5 -top-1 rounded-full bg-primary px-1 py-0.5 text-[9px] font-medium leading-none text-primary-foreground">
                  {userDraftCount}
                </span>
              )}
              {f.key === "drafts" && pendingDraftCount > 0 && (
                <span className="absolute -right-0.5 -top-1 rounded-full bg-primary px-1 py-0.5 text-[9px] font-medium leading-none text-primary-foreground">
                  {pendingDraftCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div className="mt-2 min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner className="text-muted-foreground" />
          </div>
        ) : isThreadFolder ? (
          <>
            {visibleThreads.length === 0 ? (
              <ListEmptyState
                hasAny={threads.length > 0}
                emptyLabel={
                  folder === "archive" ? "Nothing archived." : "No mail yet."
                }
                emptyHint={
                  folder === "archive"
                    ? undefined
                    : "Messages sent to this job's address will appear here."
                }
              />
            ) : (
              <div className="space-y-0.5">
                {visibleThreads.map((thread) => (
                  <ThreadRow
                    key={thread._id}
                    thread={thread}
                    folder={folder}
                    selected={thread._id === selectedThreadId}
                    onSelect={() => onSelectThread(thread._id, thread.jobId)}
                  />
                ))}
              </div>
            )}
            {jobId && (
              <LoadMore
                status={threadsQuery.status}
                onLoadMore={() => threadsQuery.loadMore(25)}
              />
            )}
          </>
        ) : folder === "userDrafts" ? (
          visibleUserDrafts.length === 0 ? (
            <ListEmptyState
              hasAny={(userDrafts ?? []).length > 0}
              emptyLabel="No drafts."
              emptyHint="Emails you start writing are saved here automatically."
            />
          ) : (
            <div className="space-y-0.5">
              {visibleUserDrafts.map((draft) => (
                <UserDraftRow
                  key={draft._id}
                  draft={draft}
                  selected={draft._id === selectedUserDraftId}
                  onSelect={() => onSelectUserDraft(draft)}
                />
              ))}
            </div>
          )
        ) : folder === "sent" ? (
          <>
            {sentMessages.length === 0 ? (
              <ListEmptyState
                hasAny={
                  (jobId ? (sentQuery.results ?? []) : (workspaceSent ?? []))
                    .length > 0
                }
                icon={Send}
                emptyLabel="Nothing sent yet."
                emptyHint="Approved drafts, composed emails, and Day One sends will appear here."
              />
            ) : (
              <div className="space-y-0.5">
                {sentMessages.map((message) => (
                  <SentRow
                    key={message._id}
                    message={message}
                    selected={message.threadId === selectedThreadId}
                    onSelect={() =>
                      onSelectThread(message.threadId, message.jobId)
                    }
                  />
                ))}
              </div>
            )}
            {jobId && (
              <LoadMore
                status={sentQuery.status}
                onLoadMore={() => sentQuery.loadMore(25)}
              />
            )}
          </>
        ) : visibleDraftCount === 0 ? (
          <ListEmptyState
            hasAny={(drafts ?? []).length > 0}
            emptyLabel="No drafts."
            emptyHint="When Morgan drafts a reply or you ask for one in chat, it appears here for approval."
          />
        ) : (
          <div className="space-y-0.5">
            {draftGroups.needsReview.length > 0 && (
              <p className="px-3 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                Needs review
              </p>
            )}
            {draftGroups.needsReview.map((draft) => (
              <DraftRow
                key={draft._id}
                draft={draft}
                selected={draft._id === selectedDraftId}
                onSelect={() => onSelectDraft(draft._id, draft.jobId)}
              />
            ))}
            {draftGroups.history.length > 0 && (
              <p className="px-3 pb-1 pt-3 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                History
              </p>
            )}
            {draftGroups.history.map((draft) => (
              <DraftRow
                key={draft._id}
                draft={draft}
                selected={draft._id === selectedDraftId}
                onSelect={() => onSelectDraft(draft._id, draft.jobId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ListEmptyState({
  hasAny,
  emptyLabel,
  emptyHint,
  icon: Icon,
}: {
  hasAny: boolean;
  emptyLabel: string;
  emptyHint?: string;
  icon?: typeof Send;
}) {
  return (
    <div className="flex h-48 flex-col items-center justify-center gap-1.5 px-4 text-center text-sm text-muted-foreground">
      {Icon && <Icon className="size-6" />}
      {hasAny ? (
        <p>No results match your search.</p>
      ) : (
        <>
          <p>{emptyLabel}</p>
          {emptyHint && <p className="text-xs">{emptyHint}</p>}
        </>
      )}
    </div>
  );
}

function LoadMore({
  status,
  onLoadMore,
}: {
  status: string;
  onLoadMore: () => void;
}) {
  if (status === "CanLoadMore") {
    return (
      <div className="flex justify-center py-2">
        <Button variant="ghost" size="sm" onClick={onLoadMore}>
          Load more
        </Button>
      </div>
    );
  }
  if (status === "LoadingMore") {
    return (
      <div className="flex justify-center py-3">
        <Spinner className="size-4 text-muted-foreground" />
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function ThreadRow({
  thread,
  folder,
  selected,
  onSelect,
}: {
  thread: MailThread;
  folder: MailFolder;
  selected: boolean;
  onSelect: () => void;
}) {
  const setThreadFolder = useMutation(api.mail.mutations.setThreadFolder);
  const snoozeThread = useMutation(api.mail.mutations.snoozeThread);
  const markThreadRead = useMutation(api.mail.mutations.markThreadRead);
  const markThreadUnread = useMutation(api.mail.mutations.markThreadUnread);

  const unread = thread.unreadCount > 0;
  const primary = (thread.participants ?? [])[0];
  const displayName =
    primary?.displayName ??
    primary?.email ??
    thread.participantEmails[0] ??
    "Unknown sender";

  // Tag chips: distinct roles across counterparties, capped at two.
  const roleTags = useMemo(() => {
    const roles: string[] = [];
    for (const participant of thread.participants ?? []) {
      for (const role of participant.roles) {
        if (!roles.includes(role)) roles.push(role);
      }
    }
    return roles.slice(0, 2);
  }, [thread.participants]);

  const archived = thread.folder === "archive";

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await setThreadFolder({
        threadId: thread._id,
        folder: archived ? "inbox" : "archive",
      });
      toast.success(archived ? "Moved to inbox" : "Archived");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not move the thread"));
    }
  };

  const handleSnooze = async (until: number) => {
    try {
      await snoozeThread({ threadId: thread._id, until });
      toast.success("Snoozed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not snooze the thread"));
    }
  };

  const handleToggleRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (unread) {
        await markThreadRead({ threadId: thread._id });
      } else {
        await markThreadUnread({ threadId: thread._id });
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not update read state"));
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "group relative flex w-full cursor-pointer gap-3 rounded-lg px-3 py-3 text-left transition-colors",
        selected ? "bg-accent/15" : "hover:bg-muted/70",
      )}
    >
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
          avatarTint(primary?.email ?? displayName),
        )}
      >
        {initialsFor(displayName)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span
            className={cn(
              "truncate text-sm font-semibold",
              unread ? "text-foreground" : "text-foreground/80",
            )}
          >
            {displayName}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground/70">
            · {formatDistanceToNow(thread.lastMessageAt, { addSuffix: false })}
          </span>
          {thread.jobName && (
            <span className="ml-auto max-w-[140px] shrink-0 truncate rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary">
              {thread.jobName}
            </span>
          )}
        </div>

        <div className="mt-1 flex items-center gap-1.5">
          {unread && (
            <span className="size-1.5 shrink-0 rounded-full bg-primary" />
          )}
          <span
            className={cn(
              "truncate text-sm",
              unread
                ? "font-semibold text-foreground"
                : "font-medium text-foreground/80",
            )}
          >
            {thread.subject || "(no subject)"}
          </span>
          {unread && thread.unreadCount > 1 && (
            <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
              {thread.unreadCount}
            </span>
          )}
        </div>

        {thread.snippet && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {thread.snippet}
          </p>
        )}

        {roleTags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {roleTags.map((role) => {
              const meta = ROLE_META[role as ContactRole];
              if (!meta) return null;
              return (
                <Badge
                  key={role}
                  color={meta.badgeColor}
                  variant="soft"
                  size="sm"
                  className="whitespace-nowrap"
                >
                  {meta.label}
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      {/* Hover actions — read toggle / archive / snooze, floating top-right */}
      <div
        className="absolute right-2 top-2 flex items-center gap-0.5 rounded-md border bg-white p-0.5 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 dark:bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        {thread.hasInbound && (
          <Button
            size="icon"
            variant="ghost"
            className="size-6"
            onClick={handleToggleRead}
            title={unread ? "Mark as read" : "Mark as unread"}
            aria-label={unread ? "Mark as read" : "Mark as unread"}
          >
            {unread ? (
              <MailOpen className="size-3.5" />
            ) : (
              <MailIcon className="size-3.5" />
            )}
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          className="size-6"
          onClick={handleArchive}
          title={archived ? "Move to inbox" : "Archive"}
          aria-label={archived ? "Move to inbox" : "Archive"}
        >
          {archived ? (
            <ArchiveRestore className="size-3.5" />
          ) : (
            <Archive className="size-3.5" />
          )}
        </Button>
        {folder !== "archive" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="size-6"
                title="Snooze"
                aria-label="Snooze"
              >
                <Clock className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                Snooze until
              </DropdownMenuLabel>
              {snoozePresets().map((preset) => (
                <DropdownMenuItem
                  key={preset.label}
                  onClick={() => void handleSnooze(preset.until)}
                  className="text-sm"
                >
                  {preset.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function SentRow({
  message,
  selected,
  onSelect,
}: {
  message: SentMessage;
  selected: boolean;
  onSelect: () => void;
}) {
  const badge = message.deliveryStatus
    ? DELIVERY_BADGE[message.deliveryStatus]
    : null;
  const firstRecipient = message.toAddresses[0] ?? "(no recipients)";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full gap-3 rounded-lg px-3 py-3 text-left transition-colors",
        selected ? "bg-accent/15" : "hover:bg-muted/70",
      )}
    >
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
          avatarTint(firstRecipient),
        )}
      >
        {initialsFor(firstRecipient)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-sm font-semibold text-foreground/90">
            {message.toAddresses.join(", ") || "(no recipients)"}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground/70">
            · {formatDistanceToNow(message.sentAt, { addSuffix: false })}
          </span>
          {message.jobName && (
            <span className="ml-auto max-w-[140px] shrink-0 truncate rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary">
              {message.jobName}
            </span>
          )}
        </div>
        <div className="mt-1 flex min-w-0 items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-foreground/80">
            {message.subject || "(no subject)"}
          </span>
          {badge && (
            <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium">
              <span
                className={cn("size-1.5 rounded-full", badge.dotClassName)}
              />
              <span className={badge.textClassName}>
                {deliveryLabel(message, badge.label)}
              </span>
            </span>
          )}
        </div>
        {message.bodyText && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {message.bodyText.slice(0, 160)}
          </p>
        )}
      </div>
    </button>
  );
}

/** A human work-in-progress draft (Drafts folder) with a discard action. */
function UserDraftRow({
  draft,
  selected,
  onSelect,
}: {
  draft: UserDraftListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const deleteUserDraft = useMutation(api.mail.userDrafts.deleteUserDraft);
  const firstRecipient = draft.to[0] ?? "(no recipients)";
  // A draft whose send was rejected sits here with its error until resent.
  const failed = draft.status === "failed";
  const failedMeta = failed ? draftStatusMeta("failed") : null;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteUserDraft({ draftId: draft._id });
      toast.success("Draft discarded");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not discard the draft"));
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "group relative flex w-full cursor-pointer gap-3 rounded-lg px-3 py-3 text-left transition-colors",
        selected ? "bg-accent/15" : "hover:bg-muted/70",
      )}
    >
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
          avatarTint(firstRecipient),
        )}
      >
        {initialsFor(firstRecipient)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-sm font-semibold text-foreground/90">
            {draft.to.join(", ") || "(no recipients)"}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground/70">
            · {formatDistanceToNow(draft._creationTime, { addSuffix: false })}
          </span>
          {failedMeta && (
            <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium">
              <span
                className={cn("size-1.5 rounded-full", failedMeta.dotClassName)}
              />
              <span className={failedMeta.textClassName}>
                {failedMeta.label}
              </span>
            </span>
          )}
          {draft.jobName && (
            <span className="ml-auto max-w-[140px] shrink-0 truncate rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary">
              {draft.jobName}
            </span>
          )}
        </div>
        <p className="mt-1 truncate text-sm font-medium text-foreground/80">
          {draft.subject || "(no subject)"}
        </p>
        {failed ? (
          <p
            className="mt-0.5 truncate text-xs text-destructive"
            title={draft.error ?? undefined}
          >
            Couldn't send: {draft.error ?? "Send failed"}
          </p>
        ) : (
          draft.bodyText && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {draft.bodyText.slice(0, 160)}
            </p>
          )
        )}
      </div>
      <div
        className="absolute right-2 top-2 flex items-center gap-0.5 rounded-md border bg-white p-0.5 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 dark:bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          size="icon"
          variant="ghost"
          className="size-6"
          onClick={handleDelete}
          title="Discard draft"
          aria-label="Discard draft"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

function DraftRow({
  draft,
  selected,
  onSelect,
}: {
  draft: MailDraft;
  selected: boolean;
  onSelect: () => void;
}) {
  const badge = draftStatusMeta(draft.status);
  const firstRecipient = draft.to[0] ?? "(no recipients)";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full gap-3 rounded-lg px-3 py-3 text-left transition-colors",
        selected ? "bg-accent/15" : "hover:bg-muted/70",
      )}
    >
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
          avatarTint(firstRecipient),
        )}
      >
        {initialsFor(firstRecipient)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-sm font-semibold text-foreground/90">
            {draft.to.join(", ") || "(no recipients)"}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground/70">
            · {formatDistanceToNow(draft._creationTime, { addSuffix: false })}
          </span>
          {draft.jobName && (
            <span className="ml-auto max-w-[140px] shrink-0 truncate rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary">
              {draft.jobName}
            </span>
          )}
        </div>
        <div className="mt-1 flex min-w-0 items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-foreground/80">
            {draft.subject || "(no subject)"}
          </span>
          <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium">
            <span className={cn("size-1.5 rounded-full", badge.dotClassName)} />
            <span className={badge.textClassName}>{badge.label}</span>
          </span>
        </div>
        {draft.bodyText && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {draft.bodyText.slice(0, 160)}
          </p>
        )}
      </div>
    </button>
  );
}
